import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminTeam, AdminTeamMember, TeamRole } from '@central/contracts';
import { Users, X } from 'lucide-react';
import { Button } from '@/components/Button';
import {
  adicionarMembro,
  alterarEquipe,
  apagarEquipe,
  chavesDeAdministracao,
  criarEquipe,
  listarEquipes,
  listarMembros,
  removerMembro,
} from '@/features/admin/admin.api';
import {
  AcoesDaLinha,
  Cabecalho,
  Campo,
  CascaDeCatalogo,
  Celula,
  Chave,
  chaveSugerida,
  CLASSE_DE_CAMPO,
  ConfirmacaoDeCatalogo,
  ContagemDeUso,
  EtiquetaDeAtividade,
  Linha,
  LinhaDeCabecalho,
  useEscritaDeCatalogo,
  type Intencao,
} from '@/features/admin/componentes';
import { Modal } from '@/features/tickets/Modal';
import { buscarPessoas } from '@/features/tickets/lookups.api';

/**
 * Equipes — as filas de atendimento.
 *
 * Sem reordenação: a equipe não tem `order` no modelo, e a lista é alfabética.
 * Ordem manual só faz sentido onde ela é oferecida como escolha numa sequência
 * — situação e classificação —, e inventá-la aqui seria uma coluna que ninguém
 * lê no lugar onde ela é gravada.
 */
const TIPOS: Array<[AdminTeam['type'], string]> = [
  ['SUPPORT', 'Suporte'],
  ['OPERATIONS', 'Operações'],
  ['PROJECT', 'Projeto'],
  ['CAB', 'Comitê de mudanças'],
  ['APPROVAL', 'Aprovação'],
];

const ROTULO_DE_TIPO = new Map(TIPOS);

export function TeamsPage() {
  const consulta = useQuery({ queryKey: chavesDeAdministracao.equipes, queryFn: listarEquipes });
  const equipes = consulta.data?.items ?? [];

  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState<AdminTeam | null>(null);
  const [gerenciandoMembros, setGerenciandoMembros] = useState<AdminTeam | null>(null);
  const [confirmando, setConfirmando] = useState<{
    registro: AdminTeam;
    intencao: Intencao;
  } | null>(null);

  const atividade = useEscritaDeCatalogo(
    ({ id, active, version }: { id: string; active: boolean; version: number }) =>
      alterarEquipe(id, { active, version }),
    () => setConfirmando(null),
  );
  const remocao = useEscritaDeCatalogo(
    (id: string) => apagarEquipe(id),
    () => setConfirmando(null),
  );

  const ocupado = atividade.mutacao.isPending || remocao.mutacao.isPending;

  return (
    <>
      <CascaDeCatalogo
        titulo="Equipes"
        resumo="As filas de atendimento. Quem é membro de cada uma é assunto da tela de pessoas."
        rotuloDoBotao="Nova equipe"
        aoCriar={() => setCriando(true)}
        consulta={consulta}
        vazio="Nenhuma equipe cadastrada. Sem equipe, todo chamado nasce sem fila."
      >
        <LinhaDeCabecalho>
          <Cabecalho>Equipe</Cabecalho>
          <Cabecalho>Chave</Cabecalho>
          <Cabecalho>Tipo</Cabecalho>
          <Cabecalho>Nível</Cabecalho>
          <Cabecalho>E-mail</Cabecalho>
          <Cabecalho>Em uso</Cabecalho>
          <Cabecalho>Estado</Cabecalho>
          <Cabecalho className="text-right">Ações</Cabecalho>
        </LinhaDeCabecalho>

        <tbody>
          {equipes.map((equipe) => (
            <Linha key={equipe.id} inativo={!equipe.active}>
              <Celula className="font-medium">{equipe.name}</Celula>
              <Celula>
                <Chave valor={equipe.key} />
              </Celula>
              <Celula className="text-xs">{ROTULO_DE_TIPO.get(equipe.type) ?? equipe.type}</Celula>
              <Celula className="text-xs text-muted-foreground">
                {equipe.supportTier ? `N${equipe.supportTier}` : <span aria-label="sem nível">—</span>}
              </Celula>
              <Celula className="text-xs text-muted-foreground">
                {equipe.email ?? <span aria-label="sem e-mail">—</span>}
              </Celula>
              <Celula>
                <ContagemDeUso uso={equipe.usage} />
              </Celula>
              <Celula>
                <EtiquetaDeAtividade ativo={equipe.active} />
              </Celula>
              <Celula>
                <div className="flex items-center justify-end gap-1">
                  <button
                    title="Gerenciar membros"
                    onClick={() => setGerenciandoMembros(equipe)}
                    className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <Users className="h-3.5 w-3.5" />
                    Membros
                  </button>
                  <AcoesDaLinha
                  registro={equipe}
                  entidade="equipe"
                  ocupado={ocupado}
                  aoEditar={() => setEditando(equipe)}
                  aoAlternarAtividade={() => {
                    if (!equipe.active) {
                      atividade.mutacao.mutate({
                        id: equipe.id,
                        active: true,
                        version: equipe.version,
                      });
                      return;
                    }
                    setConfirmando({ registro: equipe, intencao: 'desativar' });
                  }}
                  aoRemover={() => setConfirmando({ registro: equipe, intencao: 'remover' })}
                />
                </div>
              </Celula>
            </Linha>
          ))}
        </tbody>
      </CascaDeCatalogo>

      <DialogoDeEquipe
        aberto={criando || editando !== null}
        equipe={editando}
        equipes={equipes}
        aoFechar={() => {
          setCriando(false);
          setEditando(null);
        }}
      />

      <DialogoDeMembros
        equipe={gerenciandoMembros}
        aoFechar={() => setGerenciandoMembros(null)}
      />

      <ConfirmacaoDeCatalogo
        aberto={confirmando !== null}
        intencao={confirmando?.intencao ?? 'desativar'}
        entidade="equipe"
        registro={confirmando?.registro ?? null}
        erro={atividade.erro ?? remocao.erro}
        ocupado={ocupado}
        aoFechar={() => setConfirmando(null)}
        aoConfirmar={() => {
          if (!confirmando) return;
          if (confirmando.intencao === 'remover') {
            remocao.mutacao.mutate(confirmando.registro.id);
            return;
          }
          atividade.mutacao.mutate({
            id: confirmando.registro.id,
            active: false,
            version: confirmando.registro.version,
          });
        }}
      />
    </>
  );
}

const TIERS: Array<[number | null, string]> = [
  [null, 'Sem nível'],
  [1, 'N1 — Atendimento inicial'],
  [2, 'N2 — Suporte avançado'],
  [3, 'N3 — Engenharia'],
];

function DialogoDeEquipe({
  aberto,
  equipe,
  equipes,
  aoFechar,
}: {
  aberto: boolean;
  equipe: AdminTeam | null;
  equipes: AdminTeam[];
  aoFechar: () => void;
}) {
  const editando = equipe !== null;

  const [nome, setNome] = useState('');
  const [chave, setChave] = useState('');
  const [chaveTocada, setChaveTocada] = useState(false);
  const [tipo, setTipo] = useState<AdminTeam['type']>('SUPPORT');
  const [email, setEmail] = useState('');
  const [tier, setTier] = useState<number | null>(null);
  const [escalaPara, setEscalaPara] = useState('');
  const [semeado, setSemeado] = useState<string | null>(null);

  const identidade = equipe?.id ?? (aberto ? 'nova' : null);
  if (aberto && semeado !== identidade) {
    setSemeado(identidade);
    setNome(equipe?.name ?? '');
    setChave(equipe?.key ?? '');
    setChaveTocada(equipe !== null);
    setTipo(equipe?.type ?? 'SUPPORT');
    setEmail(equipe?.email ?? '');
    setTier(equipe?.supportTier ?? null);
    setEscalaPara(equipe?.escalatesToTeamId ?? '');
  }
  if (!aberto && semeado !== null) setSemeado(null);

  const escrita = useEscritaDeCatalogo(async () => {
    const contato = email.trim();
    if (equipe) {
      return alterarEquipe(equipe.id, {
        name: nome.trim(),
        type: tipo,
        ...(contato ? { email: contato } : {}),
        supportTier: tier,
        escalatesToTeamId: escalaPara || null,
        version: equipe.version,
      });
    }
    return criarEquipe({
      key: chave.trim(),
      name: nome.trim(),
      type: tipo,
      ...(contato ? { email: contato } : {}),
      ...(tier !== null ? { supportTier: tier } : {}),
      ...(escalaPara ? { escalatesToTeamId: escalaPara } : {}),
    });
  }, aoFechar);

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    escrita.mutacao.mutate();
  }

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={editando ? `Editar “${equipe.name}”` : 'Nova equipe'}
      descricao={
        editando
          ? 'A chave não muda: integração, URL e automação a usam para achar este registro.'
          : 'A chave é definitiva. O nome pode ser corrigido depois.'
      }
    >
      <form onSubmit={enviar} className="space-y-4 px-5 py-4">
        <Campo id="equipe-nome" rotulo="Nome">
          <input
            id="equipe-nome"
            value={nome}
            onChange={(evento) => {
              setNome(evento.target.value);
              if (!chaveTocada) setChave(chaveSugerida(evento.target.value));
            }}
            required
            minLength={2}
            maxLength={80}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            id="equipe-chave"
            rotulo="Chave"
            dica={editando ? 'Imutável depois de criada.' : 'Minúsculas, dígitos, ponto e hífen.'}
          >
            <input
              id="equipe-chave"
              value={chave}
              onChange={(evento) => {
                setChaveTocada(true);
                setChave(evento.target.value);
              }}
              disabled={editando}
              required
              pattern="[a-z0-9][a-z0-9.\-]*[a-z0-9]"
              minLength={2}
              maxLength={60}
              className={`${CLASSE_DE_CAMPO} font-mono text-xs`}
            />
          </Campo>

          <Campo id="equipe-tipo" rotulo="Tipo">
            <select
              id="equipe-tipo"
              value={tipo}
              onChange={(evento) => setTipo(evento.target.value as AdminTeam['type'])}
              className={CLASSE_DE_CAMPO}
            >
              {TIPOS.map(([valor, rotulo]) => (
                <option key={valor} value={valor}>
                  {rotulo}
                </option>
              ))}
            </select>
          </Campo>
        </div>

        <Campo
          id="equipe-email"
          rotulo="E-mail da equipe"
          opcional
          dica="Caixa compartilhada para onde vão as notificações da fila."
        >
          <input
            id="equipe-email"
            type="email"
            value={email}
            onChange={(evento) => setEmail(evento.target.value)}
            maxLength={320}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            id="equipe-tier"
            rotulo="Nível de suporte"
            opcional
            dica="Usado no escalonamento automático por SLA."
          >
            <select
              id="equipe-tier"
              value={tier ?? ''}
              onChange={(e) => setTier(e.target.value ? Number(e.target.value) : null)}
              className={CLASSE_DE_CAMPO}
            >
              {TIERS.map(([valor, rotulo]) => (
                <option key={String(valor)} value={valor ?? ''}>
                  {rotulo}
                </option>
              ))}
            </select>
          </Campo>

          <Campo
            id="equipe-escala-para"
            rotulo="Escala para"
            opcional
            dica="Equipe de destino no escalonamento manual ou automático."
          >
            <select
              id="equipe-escala-para"
              value={escalaPara}
              onChange={(e) => setEscalaPara(e.target.value)}
              className={CLASSE_DE_CAMPO}
            >
              <option value="">Nenhuma</option>
              {equipes
                .filter((e) => e.active && e.id !== equipe?.id)
                .map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name}
                    {e.supportTier ? ` (N${e.supportTier})` : ''}
                  </option>
                ))}
            </select>
          </Campo>
        </div>

        {escrita.erro ? (
          <p role="alert" className="text-sm text-destructive">
            {escrita.erro}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button type="button" variant="secondary" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button type="submit" loading={escrita.mutacao.isPending}>
            {editando ? 'Salvar' : 'Criar equipe'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Dialog de membros ──────────────────────────────────────────────────────

const ROTULO_DE_PAPEL: Record<TeamRole, string> = {
  MEMBER: 'Membro',
  LEAD: 'Líder',
  DISPATCHER: 'Despachante',
};

function DialogoDeMembros({
  equipe,
  aoFechar,
}: {
  equipe: AdminTeam | null;
  aoFechar: () => void;
}) {
  const clienteDeConsulta = useQueryClient();
  const aberto = equipe !== null;

  const [busca, setBusca] = useState('');
  const [userId, setUserId] = useState('');
  const [papel, setPapel] = useState<TeamRole>('MEMBER');
  const [erro, setErro] = useState<string | null>(null);

  const membros = useQuery({
    queryKey: equipe ? chavesDeAdministracao.membrosDeEquipe(equipe.id) : ['noop'],
    queryFn: () => listarMembros(equipe!.id),
    enabled: aberto,
  });

  const resultadosBusca = useQuery({
    queryKey: ['busca-pessoas', busca],
    queryFn: () => buscarPessoas(busca),
    enabled: busca.length >= 2,
    staleTime: 30_000,
  });

  const adicionar = useMutation({
    mutationFn: () => adicionarMembro(equipe!.id, { userId, role: papel }),
    onSuccess: () => {
      setUserId('');
      setBusca('');
      setErro(null);
      void clienteDeConsulta.invalidateQueries({
        queryKey: chavesDeAdministracao.membrosDeEquipe(equipe!.id),
      });
    },
    onError: (e: Error) => setErro(e.message),
  });

  const remover = useMutation({
    mutationFn: (uid: string) => removerMembro(equipe!.id, uid),
    onSuccess: () => {
      void clienteDeConsulta.invalidateQueries({
        queryKey: chavesDeAdministracao.membrosDeEquipe(equipe!.id),
      });
    },
  });

  if (!aberto) return null;

  const pessoaSelecionada = resultadosBusca.data?.find((p) => p.id === userId);

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={`Membros — ${equipe.name}`}
      descricao="Operadores internos que fazem parte desta equipe e recebem os chamados da fila."
    >
      <div className="divide-y divide-border px-5 py-4">
        {/* Lista atual */}
        {membros.data?.items.length === 0 ? (
          <p className="py-2 text-sm text-muted-foreground italic">Nenhum membro ainda.</p>
        ) : (
          <ul className="space-y-1 pb-4">
            {(membros.data?.items ?? []).map((m: AdminTeamMember) => (
              <li key={m.userId} className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-muted">
                <div>
                  <p className="text-sm font-medium">{m.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {m.email} · {ROTULO_DE_PAPEL[m.role]}
                  </p>
                </div>
                <button
                  onClick={() => remover.mutate(m.userId)}
                  disabled={remover.isPending}
                  className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  title="Remover da equipe"
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Adicionar */}
        <div className="space-y-3 pt-4">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Adicionar membro
          </p>

          <div className="space-y-2">
            <input
              placeholder="Buscar por nome ou e-mail…"
              value={busca}
              onChange={(e) => { setBusca(e.target.value); setUserId(''); }}
              className={CLASSE_DE_CAMPO}
            />
            {busca.length >= 2 && (
              <ul className="max-h-40 overflow-y-auto rounded-md border border-border bg-popover shadow-sm">
                {resultadosBusca.isPending ? (
                  <li className="px-3 py-2 text-sm text-muted-foreground">Buscando…</li>
                ) : (resultadosBusca.data ?? []).length === 0 ? (
                  <li className="px-3 py-2 text-sm text-muted-foreground">Nenhum resultado.</li>
                ) : (
                  (resultadosBusca.data ?? []).map((p) => (
                    <li key={p.id}>
                      <button
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-muted ${p.id === userId ? 'bg-primary/10 font-medium' : ''}`}
                        onClick={() => { setUserId(p.id); setBusca(p.name); }}
                      >
                        {p.name} <span className="text-xs text-muted-foreground">{p.email}</span>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>

          <div className="flex gap-2">
            <select
              value={papel}
              onChange={(e) => setPapel(e.target.value as TeamRole)}
              className={`${CLASSE_DE_CAMPO} flex-1`}
            >
              {(Object.entries(ROTULO_DE_PAPEL) as [TeamRole, string][]).map(([v, r]) => (
                <option key={v} value={v}>{r}</option>
              ))}
            </select>
            <Button
              onClick={() => adicionar.mutate()}
              disabled={!userId || !pessoaSelecionada}
              loading={adicionar.isPending}
              size="sm"
            >
              Adicionar
            </Button>
          </div>

          {erro ? <p role="alert" className="text-sm text-destructive">{erro}</p> : null}
        </div>
      </div>
    </Modal>
  );
}
